import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Globe } from 'lucide-react';
import { api } from '@/lib/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea, Toggle } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

const CATEGORIES = [
  { value: 'e-commerce', label: 'E-commerce' },
  { value: 'berita', label: 'Berita' },
  { value: 'lowongan-kerja', label: 'Lowongan Kerja' },
  { value: 'properti', label: 'Properti' },
  { value: 'kuliner', label: 'Kuliner' },
  { value: 'media-sosial', label: 'Media Sosial' },
  { value: 'keuangan', label: 'Keuangan' },
  { value: 'akademik', label: 'Akademik' },
  { value: 'lainnya', label: 'Lainnya' },
];

const LICENSES = [
  { value: 'internal', label: 'Internal - hanya untuk organisasi saya' },
  { value: 'cc0', label: 'CC0 - bebas dipakai tanpa syarat' },
  { value: 'cc-by', label: 'CC BY - bebas dipakai, wajib menyebut sumber' },
  { value: 'cc-by-sa', label: 'CC BY-SA - wajib sebut sumber & berbagi serupa' },
  { value: 'custom', label: 'Lisensi khusus - dijelaskan di disclaimer' },
];

const EMPTY_ADVANCED = {
  attribution: '',
  sourceUrl: '',
  methodology: '',
  updateFrequency: 'sekali',
  language: 'id',
  region: '',
  disclaimer: '',
  contactEmail: '',
  allowDownload: true,
  isCleaned: false,
};

/**
 * Dialog publikasi dataset ke Data Publik.
 *
 * Keterangan tambahan dipisah ke bagian yang bisa dilipat, bukan dihapus.
 * Data tanpa konteks - kapan diambil, seberapa sering diperbarui, boleh dipakai
 * untuk apa - nyaris tidak berguna bagi orang lain, tapi memaksa mengisi
 * sepuluh kolom sebelum bisa menerbitkan juga membuat orang menyerah. Jadi:
 * empat kolom wajib di depan, sisanya tersedia bagi yang mau melengkapi.
 */
export const PublishDialog = ({ open, onClose, file }) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'lainnya',
    tags: '',
    license: 'internal',
  });
  const [advanced, setAdvanced] = useState(EMPTY_ADVANCED);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!open || !file) return;

    const existing = file.dataset;
    setForm({
      title: existing?.title ?? file.filename.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' '),
      description: '',
      category: 'lainnya',
      tags: '',
      license: 'internal',
    });
    setAdvanced(EMPTY_ADVANCED);
    setShowAdvanced(false);
  }, [open, file]);

  const mutation = useMutation({
    mutationFn: () =>
      api.post(`/storage/files/${file.id}/publish`, {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        license: form.license,
        tags: form.tags
          .split(',')
          .map((t) => t.trim().toLowerCase())
          .filter(Boolean)
          .slice(0, 10),
        advanced,
      }),
    onSuccess: (response) => {
      toast.success('Dataset dipublikasikan', {
        description: `"${response.data.title}" kini bisa diakses publik.`,
      });
      queryClient.invalidateQueries({ queryKey: ['storage'] });
      queryClient.invalidateQueries({ queryKey: ['datasets'] });
      onClose();
    },
    onError: (error) => {
      const fieldErrors = error.fieldErrors ?? {};
      const first = Object.values(fieldErrors)[0];
      toast.error('Gagal mempublikasikan', { description: first ?? error.message });
    },
  });

  const setAdv = (key, value) => setAdvanced((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Publikasikan ke Data Publik"
      description="Dataset akan muncul di katalog dan bisa dipakai pengguna lain."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Batal
          </Button>
          <Button
            icon={Globe}
            onClick={() => mutation.mutate()}
            loading={mutation.isPending}
            disabled={form.title.trim().length < 3}
          >
            Publikasikan
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Judul Dataset" required>
          {(props) => (
            <Input
              {...props}
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Misal: Harga Marketplace Elektronik Agustus 2026"
              data-autofocus
            />
          )}
        </Field>

        <Field
          label="Deskripsi"
          hint="Jelaskan isi datanya dalam satu atau dua kalimat. Ini yang dibaca orang lain sebelum memakainya."
        >
          {(props) => (
            <Textarea
              {...props}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Data apa ini, dari mana, dan untuk apa biasanya dipakai?"
              rows={3}
            />
          )}
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Kategori">
            {(props) => (
              <Select
                {...props}
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                options={CATEGORIES}
              />
            )}
          </Field>

          <Field label="Tag" hint="Pisahkan dengan koma.">
            {(props) => (
              <Input
                {...props}
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
                placeholder="elektronik, harga, jakarta"
              />
            )}
          </Field>
        </div>

        <Field
          label="Lisensi"
          hint="Menentukan apa yang boleh dilakukan orang lain terhadap data ini."
        >
          {(props) => (
            <Select
              {...props}
              value={form.license}
              onChange={(e) => setForm({ ...form, license: e.target.value })}
              options={LICENSES}
            />
          )}
        </Field>

        {/* ── Keterangan tambahan ──────────────────────────────────────── */}
        <div className="rounded-lg border border-[var(--border-subtle)]">
          <button
            type="button"
            onClick={() => setShowAdvanced((v) => !v)}
            aria-expanded={showAdvanced}
            className="flex w-full items-center justify-between px-4 py-3 text-left"
          >
            <span>
              <span className="text-sm font-bold text-[var(--text-primary)]">
                Keterangan Tambahan
              </span>
              <span className="ml-2 text-xs text-[var(--text-muted)]">
                opsional, tapi sangat membantu pemakai ulang
              </span>
            </span>
            <ChevronDown
              className={cn(
                'size-4 shrink-0 text-[var(--text-muted)] transition-transform duration-200',
                showAdvanced && 'rotate-180',
              )}
              aria-hidden="true"
            />
          </button>

          {showAdvanced && (
            <div className="space-y-4 border-t border-[var(--border-subtle)] p-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Sumber Data" hint="Nama situs atau lembaga asal data.">
                  {(props) => (
                    <Input
                      {...props}
                      value={advanced.attribution}
                      onChange={(e) => setAdv('attribution', e.target.value)}
                      placeholder="marketplace.id"
                    />
                  )}
                </Field>

                <Field label="Frekuensi Pembaruan">
                  {(props) => (
                    <Select
                      {...props}
                      value={advanced.updateFrequency}
                      onChange={(e) => setAdv('updateFrequency', e.target.value)}
                      options={[
                        { value: 'sekali', label: 'Sekali ambil' },
                        { value: 'harian', label: 'Harian' },
                        { value: 'mingguan', label: 'Mingguan' },
                        { value: 'bulanan', label: 'Bulanan' },
                        { value: 'tidak-tentu', label: 'Tidak tentu' },
                      ]}
                    />
                  )}
                </Field>

                <Field label="Bahasa Isi Data">
                  {(props) => (
                    <Select
                      {...props}
                      value={advanced.language}
                      onChange={(e) => setAdv('language', e.target.value)}
                      options={[
                        { value: 'id', label: 'Indonesia' },
                        { value: 'en', label: 'Inggris' },
                        { value: 'campuran', label: 'Campuran' },
                      ]}
                    />
                  )}
                </Field>

                <Field label="Cakupan Wilayah">
                  {(props) => (
                    <Input
                      {...props}
                      value={advanced.region}
                      onChange={(e) => setAdv('region', e.target.value)}
                      placeholder="Jabodetabek"
                    />
                  )}
                </Field>
              </div>

              <Field
                label="Metode Pengumpulan"
                hint="Bagaimana data diambil dan disaring - membantu orang menilai apakah data ini cocok untuk kebutuhannya."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    value={advanced.methodology}
                    onChange={(e) => setAdv('methodology', e.target.value)}
                    placeholder="Diambil dari halaman kategori, hanya produk yang berstatus tersedia."
                    rows={2}
                  />
                )}
              </Field>

              <Field
                label="Peringatan / Disclaimer"
                hint="Batasan yang perlu dibaca sebelum data dipakai."
              >
                {(props) => (
                  <Textarea
                    {...props}
                    value={advanced.disclaimer}
                    onChange={(e) => setAdv('disclaimer', e.target.value)}
                    placeholder="Harga dapat berubah sewaktu-waktu. Bukan untuk keputusan finansial."
                    rows={2}
                  />
                )}
              </Field>

              <Field label="Email Kontak" hint="Untuk pertanyaan tentang dataset ini.">
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={advanced.contactEmail}
                    onChange={(e) => setAdv('contactEmail', e.target.value)}
                    placeholder="nama@perusahaan.com"
                  />
                )}
              </Field>

              <div className="space-y-4 border-t border-[var(--border-subtle)] pt-4">
                <Toggle
                  checked={advanced.allowDownload}
                  onChange={(v) => setAdv('allowDownload', v)}
                  label="Izinkan pengunduhan"
                  description="Kalau dimatikan, orang lain hanya bisa melihat pratinjau dan keterangannya."
                />
                <Toggle
                  checked={advanced.isCleaned}
                  onChange={(v) => setAdv('isCleaned', v)}
                  label="Data sudah dibersihkan"
                  description="Tandai bila data sudah dirapikan, bukan hasil crawl mentah."
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
